import {
  useAccountActionPermissions,
  useQuerySearchZaps,
} from "@zapier/client";
import { useRouterParams } from "@zapier/shared";
import { PENDING_FOLDER_NAME, TRASH_FOLDER_NAME } from "@zapier/utils";
import { SortDescriptor, SortDirection } from "@zapier/design-system";

export enum EmptyZapTableTypes {
  EmptySearchResults = "EmptySearchResults",
  EmptyTrash = "EmptyTrash",
  EmptyPendingApproval = "EmptyPendingApproval",
  NoSharedZaps = "NoSharedZaps",
  NoZaps = "NoZaps",
  UpgradeForSharing = "UpgradeForSharing",
  ZapNotFound = "ZapNotFound",
}

interface Input {
  isSearch?: boolean;
  order?: string;
  shared?: string;
  virtualQuery?: string;
  isLoading?: boolean;
  hasNoZaps?: boolean;
  sortDescriptor: SortDescriptor;
  details?: string;
}

export function useShowEmptyZapTable({
  order,
  isSearch,
  shared,
  virtualQuery,
  isLoading,
  hasNoZaps,
  sortDescriptor,
  details,
}: Input) {
  const { app, apps, connection, connections, lastRun, owner, owners, status } =
    useRouterParams();
  const { isSharingEnabled } = useAccountActionPermissions();

  const hasActiveFilters = !!(
    isSearch ||
    app ||
    apps ||
    connection ||
    connections ||
    lastRun ||
    owner ||
    owners ||
    status
  );

  // Distinguish "the account has no Zaps at all" from "no Zaps match the
  // active filters". Without this, a default filter such as "owned by me"
  // (applied automatically on first load) makes a brand-new user with zero
  // Zaps see the "no results, clear filters" state instead of the onboarding
  // empty state. Only fetched when the current view is empty and a filter is
  // active, so it costs nothing in the common case.
  const { response: unfilteredZaps, isLoading: isUnfilteredCountLoading } =
    useQuerySearchZaps({
      search: "",
      owner: "",
      status: "all",
      page: 1,
      pageSize: 1,
      enabled: !!hasNoZaps && !isLoading && !details && hasActiveFilters,
    });
  const accountHasNoZaps = (unfilteredZaps?.count ?? 0) === 0;

  // Exposed so the table can keep showing its skeleton (rather than a bare
  // header shell) during the brief unfiltered-count fetch.
  const isCountLoading =
    !!hasNoZaps &&
    !isLoading &&
    !details &&
    hasActiveFilters &&
    isUnfilteredCountLoading;

  const resolveEmptyTableType = (): EmptyZapTableTypes | undefined => {
    if (details && hasNoZaps && !isLoading) {
      return EmptyZapTableTypes.ZapNotFound;
    }

    // update sortDescriptor if order is provided
    if (order) {
      sortDescriptor.column = order.replace("-", "");
      sortDescriptor.direction = (
        order[0] === "-" ? "descending" : "ascending"
      ) as SortDirection;
    }

    /* Empty Search */
    if (hasActiveFilters && hasNoZaps && !isLoading) {
      // Wait for the unfiltered count before choosing between onboarding and
      // "no results", so the wrong empty state doesn't flash.
      if (isUnfilteredCountLoading) {
        return;
      }
      // The account genuinely has no Zaps — show the onboarding empty state
      // rather than telling the user to clear filters they never set.
      if (accountHasNoZaps) {
        return EmptyZapTableTypes.NoZaps;
      }
      return EmptyZapTableTypes.EmptySearchResults;
    }

    /* Empty Trash */
    if (
      !isSearch &&
      hasNoZaps &&
      !isLoading &&
      virtualQuery === TRASH_FOLDER_NAME
    ) {
      return EmptyZapTableTypes.EmptyTrash;
    }

    /* Empty Pending */
    if (
      !isSearch &&
      hasNoZaps &&
      !isLoading &&
      virtualQuery === PENDING_FOLDER_NAME
    ) {
      return EmptyZapTableTypes.EmptyPendingApproval;
    }

    /* Empty Shared List */
    if (!isSearch && hasNoZaps && !isLoading && shared === "-me") {
      if (!isSharingEnabled) {
        return EmptyZapTableTypes.UpgradeForSharing;
      }
      return EmptyZapTableTypes.NoSharedZaps;
    }

    /* Empty List */
    // Always show empty state when there are no zaps, regardless of folders
    // (Both isMPFEnabled true and false should show empty state when no zaps exist)
    if (!isSearch && hasNoZaps && !isLoading) {
      return EmptyZapTableTypes.NoZaps;
    }

    return;
  };

  return { emptyTableType: resolveEmptyTableType(), isCountLoading };
}
